import React, { useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  FaArrowRight, FaAngleRight, FaShieldAlt, FaExclamationTriangle,
} from 'react-icons/fa';
import PageNavbar from './PageNavbar';
import Footer from './Footer';
import {
  defaultViewport, staggerContainer, staggerItem, getRevealProps,
} from '../motion/scrollMotion';
import { getIndustryBySlug, industries } from '../config/industriesConfig';
import { caseStudies } from './CustomersPage';
import { useRegion } from '../context/RegionContext';
import './ServiceDetailPage.css';
import './IndustryDetailPage.css';

const IndustryDetailPage = () => {
  const { slug } = useParams();
  const industry = getIndustryBySlug(slug);
  const { region } = useRegion();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!industry) {
    return <Navigate to="/customers" replace />;
  }

  const relatedIndustries = industries.filter((i) => i.slug !== industry.slug).slice(0, 3);
  const caseStudy = industry.caseStudyRef
    ? caseStudies.find((cs) => cs.title === industry.caseStudyRef)
    : null;
  const visibleFrameworks = industry.frameworks.filter(
    (f) => f.region === 'both' || f.region === region
  );

  return (
    <div className="svc-page">
      <PageNavbar />

      <section className="svc-hero">
        <div className="svc-hero-glow" />
        <div className="svc-container svc-hero-inner">
          <motion.div className="svc-hero-text" {...getRevealProps('fadeUp')}>
            <nav className="svc-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <FaAngleRight />
              <Link to="/customers">Customers</Link>
              <FaAngleRight />
              <span>{industry.label}</span>
            </nav>
            <span className="svc-eyebrow">{industry.eyebrow}</span>
            <h1 className="svc-hero-title">{industry.heroTitle}</h1>
            <p className="svc-hero-desc">{industry.heroDesc}</p>
            <div className="svc-hero-actions">
              <Link to="/contact" className="svc-btn svc-btn-primary">
                Get In Touch <FaArrowRight />
              </Link>
              <Link to="/customers" className="svc-btn svc-btn-ghost">
                All Industries
              </Link>
            </div>
          </motion.div>

          <motion.aside className="svc-hero-card" {...getRevealProps('slideRight', 0.12)}>
            <span className="svc-card-tag">At a glance</span>
            <div className="svc-stats">
              {industry.stats.map((s) => (
                <div className="svc-stat" key={s.label}>
                  <div className="svc-stat-value">{s.value}</div>
                  <div className="svc-stat-label">{s.label}</div>
                </div>
              ))}
            </div>
          </motion.aside>
        </div>
      </section>

      {/* Risks */}
      <section className="svc-section">
        <div className="svc-container">
          <header className="svc-section-head">
            <span className="svc-section-tag">Where You're Exposed</span>
            <h2 className="svc-section-title">Risks Specific to {industry.label}</h2>
            <p className="svc-section-sub">
              Generic security advice misses the risks that are unique to how your sector actually
              operates. Here is what we look for first.
            </p>
          </header>

          <motion.div
            className="svc-feature-grid ind-risk-grid"
            initial="hidden"
            whileInView="visible"
            viewport={defaultViewport}
            variants={{ ...staggerContainer, visible: { transition: { staggerChildren: 0.08 } } }}
          >
            {industry.risks.map((r) => (
              <motion.article className="svc-feature-card" key={r.title} variants={staggerItem}>
                <div className="svc-feature-icon ind-risk-icon"><FaExclamationTriangle /></div>
                <h3 className="svc-feature-title">{r.title}</h3>
                <p className="svc-feature-desc">{r.desc}</p>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Relevant services */}
      <section className="svc-section svc-section-alt">
        <div className="svc-container">
          <header className="svc-section-head">
            <span className="svc-section-tag">How We Help</span>
            <h2 className="svc-section-title">Services Built for {industry.label}</h2>
          </header>

          <div className="svc-related-grid ind-services-grid">
            {industry.services.map((s) => (
              <Link to={s.href} className="svc-related-card" key={s.label}>
                <span>{s.label}</span>
                <FaArrowRight />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance frameworks */}
      <section className="svc-section">
        <div className="svc-container svc-why-grid">
          <motion.div className="svc-why-text" {...getRevealProps('slideLeft')}>
            <span className="svc-section-tag">Compliance & Regulatory Alignment</span>
            <h2 className="svc-section-title">Frameworks We Test and Audit Against</h2>
            <p className="svc-why-body">
              Shown for your current region ({region === 'uk' ? 'United Kingdom' : 'India'}) — switch
              the region toggle in the navigation to see the other market's requirements.
            </p>
          </motion.div>

          <motion.ul className="svc-why-list" {...getRevealProps('slideRight', 0.12)}>
            {visibleFrameworks.map((f) => (
              <li key={f.name}>
                <span className="svc-why-check"><FaShieldAlt /></span>
                {f.name}
              </li>
            ))}
          </motion.ul>
        </div>
      </section>

      {/* Case study callout */}
      {caseStudy && (
        <section className="svc-section svc-section-alt">
          <div className="svc-container">
            <header className="svc-section-head">
              <span className="svc-section-tag">In Practice</span>
              <h2 className="svc-section-title">A {industry.label} Engagement</h2>
            </header>
            <div className="ind-case-callout">
              <h3>{caseStudy.title}</h3>
              <div className="ind-case-row">
                <span>Challenge</span>
                <p>{caseStudy.challenge}</p>
              </div>
              <div className="ind-case-row">
                <span>Outcome</span>
                <p>{caseStudy.outcome}</p>
              </div>
              <Link to="/customers" className="content-card-link">
                Read more case studies <FaArrowRight />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Other industries */}
      <section className="svc-section">
        <div className="svc-container">
          <header className="svc-section-head">
            <span className="svc-section-tag">Explore More</span>
            <h2 className="svc-section-title">Other Industries We Serve</h2>
          </header>
          <div className="svc-related-grid">
            {relatedIndustries.map((ind) => (
              <Link to={`/industries/${ind.slug}`} className="svc-related-card" key={ind.slug}>
                <span>{ind.label}</span>
                <FaArrowRight />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="svc-cta">
        <div className="svc-container">
          <motion.div className="svc-cta-card" {...getRevealProps('scaleUp')}>
            <h2 className="svc-cta-title">Ready to talk about your {industry.label.toLowerCase()} environment?</h2>
            <p className="svc-cta-desc">
              We scope every engagement around your actual risk — not a generic industry package.
              We respond to every inquiry within one business day.
            </p>
            <div className="svc-cta-actions">
              <Link to="/contact" className="svc-btn svc-btn-primary">
                Book Consultation <FaArrowRight />
              </Link>
              <Link to="/services" className="svc-btn svc-btn-ghost-light">
                View All Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default IndustryDetailPage;
